import type { PricingGroupIdentity } from '@/experiences/sales-portal/domain/pricing/blocs/utils/pricing-grouping';
import type { PricingInvoiceHistoryEntry } from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import type { RecommendationAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';

export enum AgentColumnId {
  GrossMarginPercentage = 'grossMarginPercentage',
  GrossProfit = 'grossProfit',
  Id = 'id',
  Name = 'name',
  Price = 'price',
  Reason = 'reason',
  UOM = 'uom',
  WeeklyGPImpact = 'weeklyGPImpact',
  WeeklyVolume = 'weeklyVolume',
}

const LEFT_ALIGNED_AGENT_COLUMNS = new Set<string>([
  AgentColumnId.Name,
  AgentColumnId.Id,
  AgentColumnId.Reason,
  AgentColumnId.UOM,
]);

const DELTA_AGENT_COLUMNS = new Set<string>([AgentColumnId.WeeklyGPImpact]);

const INPUT_AGENT_COLUMNS = new Set<string>([
  AgentColumnId.GrossMarginPercentage,
  AgentColumnId.Price,
]);

export const isLeftAlignedAgentColumn = (columnId: string) =>
  LEFT_ALIGNED_AGENT_COLUMNS.has(columnId);

export const isDeltaAgentColumn = (columnId: string) =>
  DELTA_AGENT_COLUMNS.has(columnId);

export const isInputAgentColumn = (columnId: string) =>
  INPUT_AGENT_COLUMNS.has(columnId);

export interface PricingRecommendationRow {
  grossProfitInPercentage: number;
  invoiceHistory: PricingInvoiceHistoryEntry[];
  newPriceInMicros: number;
  peerGroup: {
    id: string;
    maxGrossProfitInPercentage: number;
    minGrossProfitInPercentage: number;
  } | null;
  reasoning: string;
  restaurantAccountId: string;
  restaurantName: string;
  restaurantUUID: string;
  // Backend status for the row. For auto-accept tasks this is the baseline for
  // an untouched row (ACCEPTED/REJECTED/OVERRIDDEN); null for a pending row.
  reviewStatus: RecommendationAction | null;
  snapshotCostInMicros: number;
  snapshotGrossProfitInPercentage: number;
  snapshotPriceInMicros: number;
  supplierUUID: string;
  uuid: string;
  variantPackExternalId: string;
  variantPackName: string;
  variantPackUOM: string;
  variantPackUUID: string;
  weeklyGPImpactInMicros: number | null;
  weeklyVolume: number | null;
}

export interface RecommendationGroup extends PricingGroupIdentity {
  aggregateWeeklyGPImpactInMicros: number;
  aggregateWeeklyVolume: number;
  itemCount: number;
  subRows: PricingRecommendationRow[];
}

export interface PublishedProgress {
  accepted: number;
  overridden: number;
  pending: number;
  rejected: number;
}

export interface AgentTaskCard {
  acceptedRecommendationsCount: number;
  autoPublishAt: string | null;
  description: string;
  lastCompletedAt: string | null;
  name: string;
  overriddenRecommendationsCount: number;
  rejectedRecommendationsCount: number;
  sortOrder: number;
  taskTypeId: string;
  totalRecommendationsCount: number;
  weeklyGPImpactInMicros: number;
}

export interface AgentTaskGroup {
  cards: AgentTaskCard[];
  taskGroup: string;
}
